/**
 * useSiteAssets — サイトアセット（ロゴ・ヒーロー背景・街区背景）の取得と適用
 *
 * Admin画面で登録された画像（GET /api/site-assets）を検証し、
 * 読み込み成功を確認できたものだけを返す（モックC v2 と同じ規律）。
 *
 * フェイルセーフ（調停原則7）:
 * - 未登録キーは null = 既定デザイン（グラデーション）で表示。これは正常系。
 * - 不正URL・読み込み失敗は console.warn で文脈付き報告し、既定デザインを維持。
 * - API障害でもホームの表示は一切ブロックしない。
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { api, API_BASE, SiteAssetKey } from '../services/api';

export type SiteAssets = Record<SiteAssetKey, string | null>;

export const EMPTY_SITE_ASSETS: SiteAssets = {
    logo: null,
    heroBg: null,
    salonBg: null,
    tokiwaBg: null,
    stageBg: null,
};

const isValidUrl = (v: unknown): v is string =>
    typeof v === 'string' && /^(https?:\/\/|\/)[^\s"']+$/.test(v);

/**
 * アセットURLを表示可能な絶対URLに解決する。
 * APIは `/file/...` の相対パスを返すが、本番はフロント(anokoe.jp)とAPI(app.anokoe.jp)が
 * 別オリジンのため、相対のままだと Pages のSPAフォールバックHTMLに化けて画像が壊れる。
 * （開発環境は API_BASE='' + viteプロキシで相対のまま動くため、必ず本番相当で検証すること）
 */
export const resolveAssetUrl = (url: string): string =>
    url.startsWith('/') ? API_BASE + url : url;

/** 読み込み成功を確認してから解決する（壊れたURLで既定デザインを失わない） */
const preload = (key: string, url: string): Promise<string | null> =>
    new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => {
            console.warn(`site-assets: "${key}" の画像を読み込めませんでした: ${url}（既定デザインを維持）`);
            resolve(null);
        };
        img.src = url;
    });

// ヘッダー（App）とホームで共用するためモジュールスコープで1回だけ取得する
let sitePromise: Promise<SiteAssets> | null = null;

const loadSiteAssets = (): Promise<SiteAssets> => {
    sitePromise ??= (async (): Promise<SiteAssets> => {
        const data = await api.getSiteAssets(); // 失敗時は {} が返る
        const entries = await Promise.all(
            (Object.keys(EMPTY_SITE_ASSETS) as SiteAssetKey[]).map(async (key): Promise<[SiteAssetKey, string | null]> => {
                const entry = data[key];
                if (!entry) return [key, null]; // 未登録 = 正常（既定デザイン）
                if (!isValidUrl(entry.url)) {
                    console.warn(`site-assets: "${key}" のURLが不正です: ${String(entry.url)}`);
                    return [key, null];
                }
                // 相対パスをAPIオリジンへ解決し、updated_at をキャッシュバスターに（差し替えの即時反映）
                const resolved = resolveAssetUrl(entry.url);
                const sep = resolved.includes('?') ? '&' : '?';
                const url = `${resolved}${sep}v=${entry.updated_at}`;
                return [key, await preload(key, url)];
            })
        );
        return Object.fromEntries(entries) as SiteAssets;
    })();
    return sitePromise;
};

/** Admin画面で差し替えた直後に再取得したい場合に使う */
export const invalidateSiteAssetsCache = () => { sitePromise = null; };

export const useSiteAssets = (): SiteAssets => {
    const [assets, setAssets] = useState<SiteAssets>(EMPTY_SITE_ASSETS);

    useEffect(() => {
        let cancelled = false;
        loadSiteAssets().then(result => { if (!cancelled) setAssets(result); });
        return () => { cancelled = true; };
    }, []);

    return assets;
};

/** CSSの --asset-layer（第1レイヤー・既定は透明）に画像を差し込むstyleを作る */
export const assetLayerStyle = (url: string | null): CSSProperties =>
    url ? ({ ['--asset-layer' as string]: `url("${url}") center / cover no-repeat` }) : {};
